// The single place that decides "which transaction sets get explained": it
// composes parse850Segments, parse852Segments, parse855Segments,
// parse856Segments, parse997Segments, parse840Segments, parse810Segments and
// parse843Segments over an envelope tree (lib/envelope.ts's output),
// attaching parsed850, parsed852, parsed855, parsed856, parsed997, parsed840,
// parsed810 and/or parsed843 to each transaction. Every downstream consumer
// (the reducer, the validator, the sections builder, the UI) imports its
// "explained transaction" type from here rather than importing parser850.ts,
// parser852.ts, parser855.ts, parser856.ts, parser997.ts, parser840.ts,
// parser810.ts or parser843.ts directly, so adding another transaction set
// later only means widening this one module.
//
// A transaction whose ST01 is none of "850", "852", "855", "856", "997",
// "840", "810" or "843" is passed through unchanged, every field left
// undefined — it's shown read-only, as today.

import type {EdiEnvelope, EdiGroup, EdiTransaction} from './envelope';
import {parse850Segments, type Parsed850} from './parser850';
import {parse852Segments, type Parsed852} from './parser852';
import {parse855Segments, type Parsed855} from './parser855';
import {parse856Segments, type Parsed856} from './parser856';
import {parse997Segments, type Parsed997} from './parser997';
import {parse840Segments, type Parsed840} from './parser840';
import {parse810Segments, type Parsed810} from './parser810';
import {parse843Segments, type Parsed843} from './parser843';
import {parse844Segments, type Parsed844} from './parser844';
import {parse845Segments, type Parsed845} from './parser845';
import {parse867Segments, type Parsed867} from './parser867';
import {parse210Segments, type Parsed210} from './parser210';

export interface EdiTransactionExplained extends EdiTransaction {
  /** Left undefined for a transaction whose ST01 isn't "850". */
  parsed850?: Parsed850;
  /** Left undefined for a transaction whose ST01 isn't "852". */
  parsed852?: Parsed852;
  /** Left undefined for a transaction whose ST01 isn't "855". */
  parsed855?: Parsed855;
  /** Left undefined for a transaction whose ST01 isn't "856". */
  parsed856?: Parsed856;
  /** Left undefined for a transaction whose ST01 isn't "997". */
  parsed997?: Parsed997;
  /** Left undefined for a transaction whose ST01 isn't "840". */
  parsed840?: Parsed840;
  /** Left undefined for a transaction whose ST01 isn't "810". */
  parsed810?: Parsed810;
  /** Left undefined for a transaction whose ST01 isn't "843". */
  parsed843?: Parsed843;
  /** Left undefined for a transaction whose ST01 isn't "844". */
  parsed844?: Parsed844;
  /** Left undefined for a transaction whose ST01 isn't "845". */
  parsed845?: Parsed845;
  /** Left undefined for a transaction whose ST01 isn't "867". */
  parsed867?: Parsed867;
  /** Left undefined for a transaction whose ST01 isn't "210". */
  parsed210?: Parsed210;
}

export interface EdiGroupExplained extends Omit<EdiGroup, 'transactions'> {
  transactions: EdiTransactionExplained[];
}

export interface EdiEnvelopeExplained extends Omit<EdiEnvelope, 'groups'> {
  groups: EdiGroupExplained[];
}

/**
 * Parses every 850, 852, 855 and 856 transaction in an envelope tree
 * (lib/envelope.ts's output), attaching
 * parsed850/parsed852/parsed855/parsed856 to each as appropriate. Any other
 * transaction set is passed through unchanged, every field left undefined.
 */
export function attachExplainedTransactions(
  envelope: EdiEnvelope
): EdiEnvelopeExplained {
  return {
    ...envelope,
    groups: envelope.groups.map(group => ({
      ...group,
      transactions: group.transactions.map(transaction => ({
        ...transaction,
        parsed850:
          transaction.transactionSetCode === '850'
            ? parse850Segments(transaction.segments)
            : undefined,
        parsed852:
          transaction.transactionSetCode === '852'
            ? parse852Segments(transaction.segments)
            : undefined,
        parsed855:
          transaction.transactionSetCode === '855'
            ? parse855Segments(transaction.segments)
            : undefined,
        parsed856:
          transaction.transactionSetCode === '856'
            ? parse856Segments(transaction.segments)
            : undefined,
        parsed997:
          transaction.transactionSetCode === '997'
            ? parse997Segments(transaction.segments)
            : undefined,
        parsed840:
          transaction.transactionSetCode === '840'
            ? parse840Segments(transaction.segments)
            : undefined,
        parsed810:
          transaction.transactionSetCode === '810'
            ? parse810Segments(transaction.segments)
            : undefined,
        parsed843:
          transaction.transactionSetCode === '843'
            ? parse843Segments(transaction.segments)
            : undefined,
        parsed844:
          transaction.transactionSetCode === '844'
            ? parse844Segments(transaction.segments)
            : undefined,
        parsed845:
          transaction.transactionSetCode === '845'
            ? parse845Segments(transaction.segments)
            : undefined,
        parsed867:
          transaction.transactionSetCode === '867'
            ? parse867Segments(transaction.segments)
            : undefined,
        parsed210:
          transaction.transactionSetCode === '210'
            ? parse210Segments(transaction.segments)
            : undefined,
      })),
    })),
  };
}
